import { Injectable, NgZone, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import centroid from '@turf/centroid';
import { LocationInterface } from '../models/location-interface';
import { getCssVariableValue } from '@fc-shared/utils/styles.utils';
import bboxPolygon from '@turf/bbox-polygon';
import type { BBox, Geometry } from 'geojson';

export enum MapMode {
  Default = 'default',
  Edit = 'edit',
  Location = 'location',
  Draw = 'draw',
}

@Injectable({
  providedIn: 'root',
})
export class LocationMapService {
  map$ = new BehaviorSubject<google.maps.Map | null>(null);
  mapObservable$: Observable<google.maps.Map | null> = this.map$.asObservable();
  placeMarker: google.maps.marker.AdvancedMarkerElement | null = null;
  private readonly editableShape = signal<
    google.maps.Polygon | google.maps.Circle | null
  >(null);
  // Emits the replacement after the edit shape is swapped, so consumers (edit
  // mode) can re-adopt it for undo/redo and re-attach their edit listeners.
  shapeConverted$ = new Subject<google.maps.Polygon | google.maps.Circle>();
  locationList: LocationInterface[] = [];
  private readonly currentMapMode = signal<MapMode | undefined>(undefined);
  private markerList: Map<number, google.maps.marker.AdvancedMarkerElement> =
    new Map();
  private mapLocationList: Map<
    number,
    google.maps.Polygon | google.maps.Circle
  > = new Map();
  private zoomListener: google.maps.MapsEventListener | null = null;
  private editColor = '#78909c';

  constructor(
    private router: Router,
    private ngZone: NgZone,
  ) {}

  static drawCircle(
    location: LocationInterface,
    map: google.maps.Map | null,
    editable = false,
  ): google.maps.Circle | undefined {
    const [lng, lat] = location.area?.coordinates ?? [];
    if (lat == null || lng == null) return undefined;
    const circleShape = new google.maps.Circle({
      strokeColor: location.type?.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: location.type?.color,
      fillOpacity: 0.3,
      map: map,
      center: new google.maps.LatLng(lat, lng),
      radius: location.radius,
      zIndex: editable ? 1 : 0,
    });
    (circleShape as any)['id'] = location.id;
    (circleShape as any)['color'] = location.type?.color;
    if (editable) {
      circleShape.setOptions({ editable: true, draggable: true });
    }
    return circleShape;
  }

  static drawPolygon(
    location: LocationInterface,
    map: google.maps.Map | null,
    editable = false,
  ): google.maps.Polygon | undefined {
    const outerRing = location.area?.coordinates?.[0];
    if (!location.type || !outerRing) return undefined;
    const locationCoordinates = outerRing.map((coordinate: number[]) => ({
      lat: coordinate[1],
      lng: coordinate[0],
    }));
    const polygon = new google.maps.Polygon({
      strokeColor: location.type?.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: location.type?.color,
      fillOpacity: 0.3,
      map: map,
      paths: locationCoordinates,
      zIndex: editable ? 1 : 0,
    });

    (polygon as any)['id'] = location.id;
    (polygon as any)['color'] = location.type?.color;
    if (editable) {
      polygon.setOptions({ editable: true, draggable: true });
    }
    return polygon;
  }

  static getBounds(polygon: google.maps.Polygon): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    const paths = polygon.getPaths();
    for (let i = 0; i < paths.getLength(); i++) {
      const path = paths.getAt(i);
      for (let j = 0; j < path.getLength(); j++) {
        bounds.extend(path.getAt(j));
      }
    }
    return bounds;
  }

  static fitBoundsToSelectedShape(
    shape: google.maps.Polygon | google.maps.Circle,
    map: google.maps.Map,
    padding?: google.maps.Padding,
  ) {
    if (!shape) return;
    const bounds =
      shape instanceof google.maps.Polygon
        ? LocationMapService.getBounds(shape)
        : shape.getBounds();
    if (bounds) map?.fitBounds(bounds, padding);
  }

  fitLocationsBounds(map: google.maps.Map): void {
    if (!this.mapLocationList.size || !map) return;
    map.fitBounds(this.getLocationsBbox());
  }

  set mapMode(mapMode: MapMode) {
    this.currentMapMode.set(mapMode);
  }

  get mapMode(): MapMode | undefined {
    return this.currentMapMode();
  }

  set shape(shape: google.maps.Polygon | google.maps.Circle | null) {
    this.editableShape.set(shape);
  }

  get shape(): google.maps.Polygon | google.maps.Circle | null {
    return this.editableShape();
  }

  removeZoomListener(): void {
    this.zoomListener?.remove();
    this.zoomListener = null;
  }

  removePlacesMarker(): void {
    if (!this.placeMarker) return;
    this.placeMarker.map = null;
    this.placeMarker = null;
  }

  deselectEditableShape() {
    this.mapLocationList.forEach((shape) =>
      shape.setOptions({
        fillColor: (shape as any)['color'],
        strokeColor: (shape as any)['color'],
        strokeOpacity: 0.8,
        fillOpacity: 0.3,
      }),
    );
    if (this.shape) {
      this.shape.setMap(null);
      this.shape = null;
    }
  }

  setEditableShape(
    location: LocationInterface,
  ): google.maps.Polygon | google.maps.Circle | null {
    this.setDrawnShapesDisabledColor();
    const map = this.map$.getValue();
    this.shape =
      (location.area?.type === 'Point'
        ? LocationMapService.drawCircle(location, map, true)
        : LocationMapService.drawPolygon(location, map, true)) ?? null;
    return this.shape;
  }

  setDrawnShapesDisabledColor() {
    this.mapLocationList.forEach((shape) =>
      shape.setOptions({
        fillColor: this.editColor,
        fillOpacity: 0.3,
        strokeColor: this.editColor,
        strokeOpacity: 0.15,
      }),
    );
  }

  clearAllShapesFromMap(): void {
    this.mapLocationList?.forEach((shape) => shape.setMap(null));
    this.removeAllMarkersFromMap();
    this.markerList.clear();
    this.mapLocationList.clear();
  }

  drawLocationList(
    locations: LocationInterface[],
    map: google.maps.Map,
    bound = true,
    editedMode = false,
    hideOnZoomOut = true,
  ): void {
    if (!map) return;
    if (!locations.length) {
      this.clearAllShapesFromMap();
      return;
    }
    this.locationList = locations;
    this.removeDrawnLocationList();
    locations.forEach((location) => this.createLocation(location, map));
    // hide default location list (setTimeout) for cd trigger
    setTimeout(() => this.hideLocationsAccordingToZoomLevel(map));
    if (bound) this.fitLocationsBounds(map);
    if (editedMode) this.setDrawnShapesDisabledColor();
    if (hideOnZoomOut) {
      if (!this.zoomListener)
        this.zoomListener = map.addListener('zoom_changed', () =>
          this.hideLocationsAccordingToZoomLevel(map),
        );
    }
  }

  getLocationsBbox(): google.maps.LatLngBounds {
    const bound = new google.maps.LatLngBounds();
    if (this.mapLocationList.size === 1) {
      const shape = this.mapLocationList.values().next().value;
      if (shape instanceof google.maps.Polygon) {
        return LocationMapService.getBounds(shape);
      }
      return shape?.getBounds() ?? bound;
    }
    this.mapLocationList.forEach((shape) => {
      if (shape instanceof google.maps.Polygon) {
        bound.extend(LocationMapService.getBounds(shape).getCenter());
      } else {
        const circleBounds = shape.getBounds();
        if (circleBounds) bound.union(circleBounds);
      }
    });
    return bound;
  }

  drawLocation(
    location: LocationInterface,
    map: google.maps.Map,
  ): google.maps.Circle | google.maps.Polygon | undefined {
    const locationShape =
      location.area?.type === 'Point'
        ? LocationMapService.drawCircle(location, map)
        : LocationMapService.drawPolygon(location, map);
    locationShape?.setMap(map);
    return locationShape;
  }

  removeDrawnLocationList(): void {
    if (this.mapLocationList.size) {
      this.mapLocationList.forEach((shape) => shape?.setMap(null));
      this.mapLocationList.clear();
    }
    if (this.markerList.size) {
      this.removeAllMarkersFromMap();
      this.markerList.clear();
    }
  }

  removeAllMarkersFromMap(): void {
    if (this.markerList?.size)
      this.markerList?.forEach((marker) => (marker.map = null));
  }

  showAllMarkers(): void {
    if (this.markerList.size) {
      this.markerList.forEach((marker) => (marker.map = this.map$.getValue()));
      this.hideLocationsAccordingToZoomLevel(this.map$.getValue());
    }
  }

  private createLocation(
    location: LocationInterface,
    map: google.maps.Map,
  ): void {
    const locationId = location.id;
    const isCircle = location.area?.type === 'Point';
    if (locationId == null || (isCircle && !location.radius)) return;
    const locationShape = isCircle
      ? LocationMapService.drawCircle(location, map)
      : LocationMapService.drawPolygon(location, map);

    const marker = this.createLocationMarker(location, map);
    if (marker) {
      marker.addListener('click', () => this.navigateToLocation(location));
      this.markerList.set(locationId, marker);
    }

    if (locationShape) {
      locationShape.addListener('click', () =>
        this.navigateToLocation(location),
      );
      this.mapLocationList.set(locationId, locationShape);
    }
  }

  navigateToLocation(location: LocationInterface): void {
    this.ngZone.run(() => {
      if (this.mapMode !== MapMode.Edit && this.mapMode !== MapMode.Draw)
        this.router.navigate(['/locations/main/location-list/', location.id]);
    });
  }

  static getShapeCenter(location: LocationInterface): google.maps.LatLng {
    const center = centroid({
      type: 'Feature',
      geometry: location.area as Geometry,
      id: location.id,
      properties: {},
    });

    return new google.maps.LatLng(
      center.geometry.coordinates[1],
      center.geometry.coordinates[0],
    );
  }

  isPointInsideShape(
    position: google.maps.LatLng,
    currentLocationShape: google.maps.Polygon | google.maps.Circle,
  ): boolean {
    if (currentLocationShape instanceof google.maps.Circle) {
      const center = currentLocationShape.getCenter();
      return (
        !!center &&
        google.maps.geometry.spherical.computeDistanceBetween(
          center,
          position,
        ) < currentLocationShape.getRadius()
      );
    } else {
      return google.maps.geometry.poly.containsLocation(
        position,
        currentLocationShape,
      );
    }
  }

  private hideLocationsAccordingToZoomLevel(map: google.maps.Map | null): void {
    if (!map) return;
    const projection = map.getProjection();
    const zoom = map.getZoom();
    if (!projection || zoom == null) return;
    const zoomScale = Math.pow(2, zoom);
    this.mapLocationList.forEach((shape) => {
      const polyMinSize = 50;
      const bounds =
        shape instanceof google.maps.Polygon
          ? LocationMapService.getBounds(shape)
          : shape.getBounds();
      if (!bounds) return;
      const swPx = projection.fromLatLngToPoint(bounds.getSouthWest());
      const nePx = projection.fromLatLngToPoint(bounds.getNorthEast());
      if (!swPx || !nePx) return;
      const pixelWidth = Math.round(Math.abs((nePx.x - swPx.x) * zoomScale));
      const pixelHeight = Math.round(Math.abs((swPx.y - nePx.y) * zoomScale));
      if (
        this.mapMode === MapMode.Default ||
        this.mapMode === MapMode.Location ||
        !this.mapMode
      ) {
        pixelWidth < polyMinSize && pixelHeight < polyMinSize
          ? this.showMarker(map, (shape as any)['id'])
          : this.hideMarker((shape as any)['id']);
        shape.setOptions({
          visible: !(pixelWidth < polyMinSize && pixelHeight < polyMinSize),
        });
      } else {
        shape.setOptions({ visible: true });
      }
    });
  }

  private hideMarker(id: number): void {
    const marker = this.markerList.get(id);
    if (marker) marker.map = null;
  }

  private showMarker(map: google.maps.Map, id: number): void {
    const marker = this.markerList.get(id);
    if (marker && !marker.map) marker.map = map;
  }

  private createLocationMarker(
    location: LocationInterface,
    map: google.maps.Map,
  ): google.maps.marker.AdvancedMarkerElement | undefined {
    if (!location.type) return undefined;

    const position = LocationMapService.getShapeCenter(location);
    const iconVariable = getCssVariableValue(`--${location.type.icon}`);
    const iconString = `<i class="fc-icon" style="
        background: ${location.type?.color}; mask-image: url(${iconVariable}); -webkit-mask-image: url(${iconVariable});
        width: 18px; height: 18px;
      "></i>`;
    const content = document.createElement('div');
    content.classList.add('location-marker');
    content.innerHTML = `
      <div class="location-icon">${iconString}</div>
      <svg class="location-marker-svg" width="40" height="47" viewBox="0 0 40 47" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g>
            <path d="M36 16C36 20.2608 34.3345 24.1327 31.619 27C28.7028 30.0792 31.619 27 20 38.5C8.381 27 11.2972 30.0792 8.381 27C5.6655 24.1327 4 20.2608 4 16C4 7.16344 11.1634 0 20 0C28.8366 0 36 7.16344 36 16Z"
                  fill="${location.type?.color}"/>
        </g>
        <circle cx="20" cy="16" r="13" fill="white"/>
      </svg>
    `;

    return new google.maps.marker.AdvancedMarkerElement({
      content,
      position,
      map: this.mapMode === MapMode.Location ? map : null,
    });
  }

  convertToPolygon() {
    const currentShape = this.shape;

    if (currentShape instanceof google.maps.Circle) {
      const center = currentShape.getCenter();
      if (!center) return;
      const radius = currentShape.getRadius();

      // Calculate the bounding box for the circle
      // Convert radius from meters to degrees (approximate)
      const latOffset = radius / 1000 / 111.32; // km to degrees latitude
      const lngOffset =
        radius / 1000 / (111.32 * Math.cos((center.lat() * Math.PI) / 180)); // km to degrees longitude

      // Create bounding box [west, south, east, north]
      const bbox: BBox = [
        center.lng() - lngOffset,
        center.lat() - latOffset,
        center.lng() + lngOffset,
        center.lat() + latOffset,
      ];

      const squarePolygon = bboxPolygon(bbox);
      const coordinates = squarePolygon.geometry.coordinates[0].map(
        (coord: number[]) => ({
          lat: coord[1],
          lng: coord[0],
        }),
      );
      this.replaceShapeWithPolygon(coordinates);
    }
  }

  replaceShapeWithPolygon(path: google.maps.LatLngLiteral[]): void {
    const options = this.getReplacementShapeOptions();
    if (!options) return;
    this.swapShape(new google.maps.Polygon({ ...options, paths: path }));
  }

  replaceShapeWithCircle(
    center: google.maps.LatLngLiteral,
    radius: number,
  ): void {
    const options = this.getReplacementShapeOptions();
    if (!options) return;
    this.swapShape(new google.maps.Circle({ ...options, center, radius }));
  }

  private getReplacementShapeOptions(): google.maps.PolygonOptions | null {
    const currentShape = this.shape;
    if (!currentShape) return null;
    const color = (currentShape as any)['color'];
    return {
      strokeColor: currentShape.get('strokeColor') ?? color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: currentShape.get('fillColor') ?? color,
      fillOpacity: 0.3,
      map: currentShape.getMap(),
      zIndex: currentShape.get('zIndex') ?? 1,
      editable: currentShape.getEditable(),
      draggable: currentShape.getDraggable(),
    };
  }

  private swapShape(
    replacement: google.maps.Polygon | google.maps.Circle,
  ): void {
    const currentShape = this.shape;
    if (!currentShape) return;
    (replacement as any)['id'] = (currentShape as any)['id'];
    (replacement as any)['color'] = (currentShape as any)['color'];

    currentShape.setMap(null);
    this.shape = replacement;
    this.shapeConverted$.next(replacement);
  }
}
